import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateSamplesBatch } from '../../api/data';
import {
  LambdaMethod,
  ProjectSample,
  Sample,
} from '../../@types/samples/types';
import { useAppContext } from '../../providers/AppContext';
import { useSelected } from '../../providers/selectedContext';
import { useSampleHistory } from '../CapillaryPressurePage/useSampleHistory';
import {
  buildBatchPayload,
  pickBestLambdaMethod,
  toRunResult,
} from './batchRunner';
import {
  AnalysisMethod,
  BatchProgress,
  BatchStatus,
  KrRunMode,
  SampleRunResult,
  StatusFilter,
  isKrMethod,
} from './types';
import { matchesMethodInput } from './analysisCategory';

const BATCH_CONCURRENCY = 3;

const mergeSavedProjectSample = (
  existing: ProjectSample,
  saved: Sample
): ProjectSample => ({
  ...existing,
  ...saved,
  performed_analyses:
    saved.performed_analyses ?? existing.performed_analyses ?? [],
  selectedAnalyses: existing.selectedAnalyses,
  selected: existing.selected,
});

/** The four Brooks-Corey solvers, in display order, for "compare all". */
const LAMBDA_COMPARE_METHODS: LambdaMethod[] = [
  'mean_slope',
  'ols_loglog',
  'nlsq',
  'weighted_nlsq',
];

/** Per-sample map of each solver method's preview result (compare mode). */
type MethodResults = Partial<Record<LambdaMethod, SampleRunResult>>;

type ResultsByMethod = Record<AnalysisMethod, Map<string, SampleRunResult>>;
type UidsByMethod = Record<AnalysisMethod, Set<string>>;

const emptyResults = (): ResultsByMethod => ({
  liao: new Map(),
  lambda: new Map(),
  'water-oil': new Map(),
  'gas-oil': new Map(),
});

const emptyUids = (): UidsByMethod => ({
  liao: new Set(),
  lambda: new Set(),
  'water-oil': new Set(),
  'gas-oil': new Set(),
});

const ALL_METHODS: AnalysisMethod[] = [
  'liao',
  'lambda',
  'water-oil',
  'gas-oil',
];

/** A real (non-null) stored result for the given methodology — persisting a
 * fresh one would overwrite it. `performed_analyses` is intentionally NOT
 * used: the backend flags a strategy even on a skipped (no-data) calculation,
 * which has no result to overwrite. Kr samples with any manual `true_*`
 * refinement also count: auto-refinement on save would replace it. */
const hasStoredResult = (sample: Sample, method: AnalysisMethod) => {
  if (method === 'lambda') {
    return sample.analysis_values?.lambda_values != null;
  }
  if (method === 'water-oil') {
    const values = sample.k_rel_values?.water_oil;
    return (
      values != null &&
      (values.no_w != null ||
        values.true_sor_w != null ||
        values.true_krw != null ||
        values.true_no_w != null)
    );
  }
  if (method === 'gas-oil') {
    const values = sample.k_rel_values?.gas_oil;
    return (
      values != null &&
      (values.no_g != null ||
        values.true_sor_g != null ||
        values.true_krg != null ||
        values.true_no_g != null)
    );
  }
  return sample.analysis_values?.liao != null;
};

/**
 * Orchestration state for the Multi Analysis page: sample selection, batch
 * execution over the active methodology (Liao or Brooks-Corey/lambda),
 * per-sample results/validation kept separately per methodology, review
 * flags, and navigation into the single-sample workflow.
 *
 * Batch results are intentionally kept out of AppContext to avoid
 * re-rendering the whole app while hundreds of samples stream in; only
 * the sample being opened in Capillary Pressure is written back.
 */
export function useMultiLiaoAnalysis(projectUid: string | null) {
  const navigate = useNavigate();
  const { data, updateData } = useAppContext();
  const { setSelectedInfo } = useSelected();
  const { addToHistory } = useSampleHistory(projectUid);

  const [samplesByUid, setSamplesByUid] = useState<Map<string, Sample>>(
    () => new Map()
  );
  const [checkedUids, setCheckedUids] = useState<Set<string>>(() => new Set());
  // The active methodology. Preview/Save run only this one; each methodology
  // keeps its own results so switching back doesn't lose the other's batch.
  const [method, setMethodState] = useState<AnalysisMethod>('liao');
  // Brooks-Corey solver method applied to the whole batch (lambda only).
  // Defaults to the recommended `nlsq`; each sample still auto-selects its
  // own point subset. Point choice is per-sample; the solver is per-batch.
  const [lambdaMethod, setLambdaMethodState] = useState<LambdaMethod>('nlsq');
  // Opt-in: run all four solvers per sample and auto-pick the best fit_rmse.
  const [compareAllMethods, setCompareAllMethodsState] = useState(false);
  // Kr batch run mode (water-oil / gas-oil): full auto-refinement (default)
  // or lab baseline only (the existing single-sample lab computation).
  const [krRunMode, setKrRunModeState] = useState<KrRunMode>('auto');
  // Per-sample results for every solver method (lambda compare mode only).
  const [methodResultsByUid, setMethodResultsByUid] = useState<
    Map<string, MethodResults>
  >(() => new Map());
  // Per-sample chosen solver (auto-best or user override) used on save.
  const [chosenMethodBySample, setChosenMethodBySample] = useState<
    Map<string, LambdaMethod>
  >(() => new Map());
  const [resultsByMethod, setResultsByMethod] = useState<ResultsByMethod>(
    emptyResults
  );
  const [batchStatus, setBatchStatus] = useState<BatchStatus>('idle');
  const [progress, setProgress] = useState<BatchProgress>({
    done: 0,
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [inspectedUid, setInspectedUid] = useState<string | null>(null);
  // Per-sample Pce inputs (editable on each chart; Liao-only). An empty value
  // means automatic optimization. Positive stored values remain fixed; legacy
  // stored zero values are treated as automatic unless explicitly re-entered.
  const [pceBySample, setPceBySample] = useState<Map<string, string>>(
    () => new Map()
  );
  // Per methodology: samples that already had a stored result when they
  // entered the selection — persisting a fresh one would overwrite it.
  const [preexistingByMethod, setPreexistingByMethod] = useState<UidsByMethod>(
    emptyUids
  );
  const abortRef = useRef<AbortController | null>(null);

  const results = resultsByMethod[method];
  const preexistingUids = preexistingByMethod[method];

  const setMethod = useCallback(
    (next: AnalysisMethod) => {
      // Don't swap methodologies mid-run; the batch belongs to one of them.
      if (batchStatus === 'running') return;
      setMethodState(next);
      // Comparison data is lambda-run-specific; drop it when the methodology
      // changes so it can't bleed across methodologies.
      setMethodResultsByUid(new Map());
      setChosenMethodBySample(new Map());
    },
    [batchStatus]
  );

  const setLambdaMethod = useCallback(
    (next: LambdaMethod) => {
      // Same rule as the methodology toggle: the running batch owns its solver.
      if (batchStatus === 'running') return;
      setLambdaMethodState(next);
    },
    [batchStatus]
  );

  const setCompareAllMethods = useCallback(
    (next: boolean) => {
      if (batchStatus === 'running') return;
      setCompareAllMethodsState(next);
    },
    [batchStatus]
  );

  const setKrRunMode = useCallback(
    (next: KrRunMode) => {
      // Same rule as the other batch options: the running batch owns them.
      if (batchStatus === 'running') return;
      setKrRunModeState(next);
    },
    [batchStatus]
  );

  /** Per-sample comparison data for the inspected sample (compare mode). */
  const methodResultsFor = useCallback(
    (uid: string): MethodResults | undefined => methodResultsByUid.get(uid),
    [methodResultsByUid]
  );

  /** Update one methodology's results map immutably. */
  const updateResults = useCallback(
    (
      target: AnalysisMethod,
      updater: (
        prev: Map<string, SampleRunResult>
      ) => Map<string, SampleRunResult>
    ) => {
      setResultsByMethod(prev => ({
        ...prev,
        [target]: updater(prev[target]),
      }));
    },
    []
  );

  /**
   * Adopt one solver method's result for a sample (auto-best override in
   * compare mode). Mirrors the chosen method's preview into the displayed
   * results map so the grid, aggregate and save all follow the pick.
   */
  const chooseMethod = useCallback(
    (uid: string, chosen: LambdaMethod) => {
      const result = methodResultsByUid.get(uid)?.[chosen];
      if (!result) return;
      setChosenMethodBySample(prev => new Map(prev).set(uid, chosen));
      updateResults('lambda', prev => new Map(prev).set(uid, result));
    },
    [methodResultsByUid, updateResults]
  );

  const samples = useMemo(
    () => Array.from(samplesByUid.values()),
    [samplesByUid]
  );

  /**
   * Samples that carry the INPUT data the active methodology needs (Pc input
   * for Liao/Brooks-Corey, water-oil / gas-oil input for the Kr methods).
   * Samples without the relevant input are never shown for that methodology.
   */
  const methodSamples = useMemo(
    () => samples.filter(sample => matchesMethodInput(sample, method)),
    [samples, method]
  );

  /** Method-scoped samples surviving the status view filter, in selection order. */
  const visibleSamples = useMemo(() => {
    return methodSamples.filter(sample => {
      if (statusFilter === 'all') return true;
      const result = results.get(sample.uid);
      if (statusFilter === 'unreviewed') return !result || !result.reviewed;
      return result?.status === statusFilter;
    });
  }, [methodSamples, results, statusFilter]);

  /** Merge samples into the selection (checked by default, like IP). */
  const addToSelection = useCallback((picked: Sample[]) => {
    if (picked.length === 0) return;
    setSamplesByUid(prev => {
      const next = new Map(prev);
      picked.forEach(sample => next.set(sample.uid, sample));
      return next;
    });
    setCheckedUids(prev => {
      const next = new Set(prev);
      picked.forEach(sample => next.add(sample.uid));
      return next;
    });
    setPreexistingByMethod(prev => {
      const next = Object.fromEntries(
        ALL_METHODS.map(key => [key, new Set(prev[key])])
      ) as UidsByMethod;
      picked.forEach(sample => {
        ALL_METHODS.forEach(key => {
          if (hasStoredResult(sample, key)) next[key].add(sample.uid);
        });
      });
      return next;
    });
  }, []);

  /**
   * Replace the whole selection with `picked` (used by the well / all-samples
   * dropdown). Unlike addToSelection this discards the previous group, so
   * switching wells swaps their samples rather than accumulating them. Preview
   * results (of both methodologies) for samples still present are kept.
   */
  const setSelection = useCallback((picked: Sample[]) => {
    abortRef.current?.abort();
    const nextSamples = new Map(picked.map(sample => [sample.uid, sample]));
    setSamplesByUid(nextSamples);
    setCheckedUids(new Set(nextSamples.keys()));
    setPreexistingByMethod(
      Object.fromEntries(
        ALL_METHODS.map(key => [
          key,
          new Set(
            picked.filter(s => hasStoredResult(s, key)).map(s => s.uid)
          ),
        ])
      ) as UidsByMethod
    );
    setResultsByMethod(prev => {
      const keep = (source: Map<string, SampleRunResult>) => {
        const next = new Map<string, SampleRunResult>();
        picked.forEach(sample => {
          const existing = source.get(sample.uid);
          if (existing) next.set(sample.uid, existing);
        });
        return next;
      };
      return Object.fromEntries(
        ALL_METHODS.map(key => [key, keep(prev[key])])
      ) as ResultsByMethod;
    });
    setMethodResultsByUid(new Map());
    setChosenMethodBySample(new Map());
    setInspectedUid(null);
    setBatchStatus('idle');
    setProgress({ done: 0, total: 0 });
  }, []);

  const clearSelection = useCallback(() => {
    abortRef.current?.abort();
    setSamplesByUid(new Map());
    setCheckedUids(new Set());
    setResultsByMethod(emptyResults());
    setPreexistingByMethod(emptyUids());
    setPceBySample(new Map());
    setMethodResultsByUid(new Map());
    setChosenMethodBySample(new Map());
    setBatchStatus('idle');
    setProgress({ done: 0, total: 0 });
    setInspectedUid(null);
  }, []);

  // The selection is project-scoped: when the active project changes, drop
  // everything so a stale batch from another project can't leak across.
  const prevProjectRef = useRef(projectUid);
  useEffect(() => {
    if (prevProjectRef.current !== projectUid) {
      prevProjectRef.current = projectUid;
      clearSelection();
    }
  }, [projectUid, clearSelection]);

  const toggleChecked = useCallback((uid: string) => {
    setCheckedUids(prev => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  }, []);

  const checkAll = useCallback(() => {
    // View filters never alter batch scope. "Select All" means every loaded
    // sample, including cards currently hidden by Show/status filters.
    setCheckedUids(new Set(samplesByUid.keys()));
  }, [samplesByUid]);

  const checkNone = useCallback(() => {
    setCheckedUids(new Set());
  }, []);

  /**
   * Select exactly the samples currently on screen — the ones surviving the
   * active methodology + status view filters. Lets the user act on a subset
   * (e.g. only the "Review" cards) without touching the hidden ones.
   */
  const checkVisible = useCallback(() => {
    setCheckedUids(new Set(visibleSamples.map(sample => sample.uid)));
  }, [visibleSamples]);

  /**
   * Checked samples the active methodology can actually run — i.e. those that
   * carry its required input data. A sample selected while another methodology
   * was active (or with no matching input) is silently excluded so the batch
   * never dispatches runs that are guaranteed to fail (e.g. Liao on a sample
   * with no Pc input). This is the true preview/run/save scope.
   */
  const runnableCheckedUids = useMemo(
    () =>
      methodSamples
        .filter(sample => checkedUids.has(sample.uid))
        .map(sample => sample.uid),
    [methodSamples, checkedUids]
  );

  /** Effective Pce input string for a sample (edit override → positive stored → Auto). */
  const pceValue = useCallback(
    (uid: string): string => {
      const override = pceBySample.get(uid);
      if (override !== undefined) return override;
      const stored = samplesByUid.get(uid)?.analysis_values?.liao?.pce;
      return stored != null && Number.isFinite(stored) && stored > 0
        ? String(stored)
        : '';
    },
    [pceBySample, samplesByUid]
  );

  const setPce = useCallback((uid: string, value: string) => {
    setPceBySample(prev => new Map(prev).set(uid, value));
  }, []);

  const resolvedPce = useCallback(
    (uid: string): number | null => {
      const override = pceBySample.get(uid);
      if (override !== undefined) {
        if (override.trim() === '') return null;
        const parsed = Number(override);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      }
      const stored = samplesByUid.get(uid)?.analysis_values?.liao?.pce;
      return typeof stored === 'number' && Number.isFinite(stored) && stored > 0
        ? stored
        : null;
    },
    [pceBySample, samplesByUid]
  );

  /**
   * PREVIEW: compute the active methodology for the given samples WITHOUT
   * persisting. Results land in that methodology's results map for review;
   * stored samples (`samplesByUid`) are left untouched so nothing is
   * overwritten until the user saves.
   */
  const run = useCallback(
    async (uids: string[]) => {
      if (uids.length === 0 || batchStatus === 'running') return;

      // ── Compare mode (Brooks-Corey): fit every solver, auto-pick the best. ──
      if (method === 'lambda' && compareAllMethods) {
        const controller = new AbortController();
        abortRef.current = controller;
        setBatchStatus('running');
        setProgress({
          done: 0,
          total: uids.length * LAMBDA_COMPARE_METHODS.length,
        });
        updateResults('lambda', prev => {
          const next = new Map(prev);
          uids.forEach(uid =>
            next.set(uid, {
              sampleUid: uid,
              status: 'pending',
              reasons: [],
              reviewed: false,
              saved: false,
            })
          );
          return next;
        });
        setMethodResultsByUid(prev => {
          const next = new Map(prev);
          uids.forEach(uid => next.set(uid, {}));
          return next;
        });

        // Accumulate every method's result locally, then commit the best fit
        // per sample. One pooled pass per method (sequential to bound load).
        const perUid = new Map<string, MethodResults>(
          uids.map(uid => [uid, {}])
        );
        for (const solver of LAMBDA_COMPARE_METHODS) {
          if (controller.signal.aborted) break;
          const payloads = uids.map(uid =>
            buildBatchPayload(uid, 'lambda', resolvedPce(uid), true, solver)
          );
          await calculateSamplesBatch(payloads, {
            signal: controller.signal,
            concurrency: BATCH_CONCURRENCY,
            onResult: outcome => {
              setProgress(prev => ({ ...prev, done: prev.done + 1 }));
              const result = toRunResult(outcome, 'lambda');
              const bucket = perUid.get(outcome.sample_uid) ?? {};
              bucket[solver] = result;
              perUid.set(outcome.sample_uid, bucket);
              setMethodResultsByUid(prev => {
                const next = new Map(prev);
                next.set(outcome.sample_uid, {
                  ...(next.get(outcome.sample_uid) ?? {}),
                  [solver]: result,
                });
                return next;
              });
            },
          });
        }

        // Auto-select the best solver per sample and surface it as the result.
        setChosenMethodBySample(prev => {
          const next = new Map(prev);
          uids.forEach(uid =>
            next.set(uid, pickBestLambdaMethod(perUid.get(uid) ?? {}))
          );
          return next;
        });
        updateResults('lambda', prev => {
          const next = new Map(prev);
          uids.forEach(uid => {
            const per = perUid.get(uid) ?? {};
            const best = per[pickBestLambdaMethod(per)];
            if (best) next.set(uid, best);
          });
          return next;
        });

        setBatchStatus(controller.signal.aborted ? 'cancelled' : 'done');
        return;
      }

      const activeMethod = method;
      const controller = new AbortController();
      abortRef.current = controller;
      setBatchStatus('running');
      setProgress({ done: 0, total: uids.length });
      updateResults(activeMethod, prev => {
        const next = new Map(prev);
        uids.forEach(uid => {
          next.set(uid, {
            sampleUid: uid,
            status: 'pending',
            reasons: [],
            reviewed: false,
            saved: false,
          });
        });
        return next;
      });

      const payloads = uids.map(uid =>
        buildBatchPayload(
          uid,
          activeMethod,
          resolvedPce(uid),
          true,
          lambdaMethod,
          // Preview never overwrites: existing manual refinement shows as
          // 'preserved' so the reviewer sees exactly what save would keep.
          { autoRefine: krRunMode === 'auto', overwriteRefinement: false }
        )
      );
      await calculateSamplesBatch(payloads, {
        signal: controller.signal,
        concurrency: BATCH_CONCURRENCY,
        onResult: outcome => {
          setProgress(prev => ({ ...prev, done: prev.done + 1 }));
          updateResults(activeMethod, prev => {
            const next = new Map(prev);
            next.set(outcome.sample_uid, toRunResult(outcome, activeMethod));
            return next;
          });
        },
      });

      setBatchStatus(controller.signal.aborted ? 'cancelled' : 'done');
    },
    [
      batchStatus,
      method,
      lambdaMethod,
      compareAllMethods,
      krRunMode,
      resolvedPce,
      updateResults,
    ]
  );

  const runChecked = useCallback(() => {
    // Status chips are view-only, but the methodology scope is real: only run
    // checked samples that carry this methodology's required input, so samples
    // without it are never dispatched (and can't fail the batch).
    run(runnableCheckedUids);
  }, [run, runnableCheckedUids]);

  const rerun = useCallback((uids: string[]) => run(uids), [run]);

  const failedUids = useMemo(
    () =>
      Array.from(results.values())
        .filter(result => result.status === 'failed')
        .map(result => result.sampleUid),
    [results]
  );

  const rerunFailed = useCallback(() => {
    run(failedUids);
  }, [run, failedUids]);

  /**
   * SAVE (commit): persist the previewed results for the given samples. This
   * is the only path that writes to the backend — it re-runs the active
   * methodology with `preview=false`. On success the sample becomes the new
   * stored truth and its overwrite tag (for this methodology) is cleared.
   */
  const save = useCallback(
    async (uids: string[]) => {
      if (uids.length === 0 || batchStatus === 'running') return;

      const activeMethod = method;
      const controller = new AbortController();
      abortRef.current = controller;
      setBatchStatus('running');
      setProgress({ done: 0, total: uids.length });

      const payloads = uids.map(uid =>
        buildBatchPayload(
          uid,
          activeMethod,
          resolvedPce(uid),
          false,
          // Persist the per-sample chosen solver (auto-best or user override in
          // compare mode); otherwise the single batch-level solver.
          chosenMethodBySample.get(uid) ?? lambdaMethod,
          // Save runs behind the "Overwrites saved" confirm, so replacing an
          // existing (manual) refinement is authorized here.
          { autoRefine: krRunMode === 'auto', overwriteRefinement: true }
        )
      );
      await calculateSamplesBatch(payloads, {
        signal: controller.signal,
        concurrency: BATCH_CONCURRENCY,
        onResult: outcome => {
          setProgress(prev => ({ ...prev, done: prev.done + 1 }));
          if (!outcome.ok || !outcome.sample) {
            updateResults(activeMethod, prev => {
              const next = new Map(prev);
              next.set(outcome.sample_uid, toRunResult(outcome, activeMethod));
              return next;
            });
            return;
          }
          const saved = outcome.sample;
          updateResults(activeMethod, prev => {
            const next = new Map(prev);
            const previous = next.get(outcome.sample_uid);
            next.set(
              outcome.sample_uid,
              toRunResult(
                outcome,
                activeMethod,
                previous?.reviewed ?? false,
                true
              )
            );
            return next;
          });
          // The persisted sample is now the stored truth; clear its overwrite
          // tag for this methodology.
          setSamplesByUid(prev => {
            if (!prev.has(saved.uid)) return prev;
            const next = new Map(prev);
            next.set(saved.uid, saved);
            return next;
          });
          updateData(previous => ({
            projectSamples: previous.projectSamples.map(sample =>
              sample.uid === saved.uid
                ? mergeSavedProjectSample(sample, saved)
                : sample
            ),
            wellSamples: previous.wellSamples.map(sample =>
              sample.uid === saved.uid ? { ...sample, ...saved } : sample
            ),
          }));
          setPreexistingByMethod(prev => {
            if (!prev[activeMethod].has(saved.uid)) return prev;
            const nextSet = new Set(prev[activeMethod]);
            nextSet.delete(saved.uid);
            return { ...prev, [activeMethod]: nextSet };
          });
        },
      });

      setBatchStatus(controller.signal.aborted ? 'cancelled' : 'done');
    },
    [
      batchStatus,
      method,
      lambdaMethod,
      chosenMethodBySample,
      resolvedPce,
      updateData,
      updateResults,
    ]
  );

  /**
   * UIDs eligible to save: checked, with a non-failed, unsaved preview.
   * Visibility is deliberately irrelevant: Show/status filters only control
   * which cards are rendered and never change batch operations.
   */
  const savableUids = useMemo(
    () =>
      runnableCheckedUids.filter(uid => {
        const result = results.get(uid);
        return (
          result &&
          !result.saved &&
          (result.status === 'valid' || result.status === 'warning')
        );
      }),
    [runnableCheckedUids, results]
  );

  /** Of the savable UIDs, those that would overwrite stored analyses. */
  const overwriteUids = useMemo(
    () => savableUids.filter(uid => preexistingUids.has(uid)),
    [savableUids, preexistingUids]
  );

  const saveChecked = useCallback(() => {
    save(savableUids);
  }, [save, savableUids]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const toggleReviewed = useCallback(
    (uid: string) => {
      updateResults(method, prev => {
        const current = prev.get(uid);
        if (!current) return prev;
        const next = new Map(prev);
        next.set(uid, { ...current, reviewed: !current.reviewed });
        return next;
      });
    },
    [method, updateResults]
  );

  const statusCounts = useMemo(() => {
    const counts = { valid: 0, warning: 0, failed: 0, reviewed: 0 };
    results.forEach(result => {
      if (result.status === 'valid') counts.valid += 1;
      if (result.status === 'warning') counts.warning += 1;
      if (result.status === 'failed') counts.failed += 1;
      if (result.reviewed) counts.reviewed += 1;
    });
    return counts;
  }, [results]);

  /**
   * Mean fit metric across successful runs of the active methodology — the
   * IP "Error" readout analogue. Liao → mean fit RMSE; Brooks-Corey and the
   * Kr methodologies → mean R² of their primary log-log regression.
   */
  const aggregateFit = useMemo(() => {
    const values: number[] = [];
    results.forEach(result => {
      const outcome = result.sample?.analysis_outcomes?.[method];
      const value =
        method === 'lambda' || isKrMethod(method)
          ? outcome?.r_squared
          : outcome?.fit_rmse;
      if (typeof value === 'number' && Number.isFinite(value)) {
        values.push(value);
      }
    });
    if (values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }, [results, method]);

  /**
   * Hand a sample over to the single-sample workflow of the active
   * methodology (Capillary Pressure for Liao/Brooks-Corey, Kr Analysis for
   * the Kr methods), following the sample-loading contract documented in
   * src/pages/CLAUDE.md.
   */
  const openInCapillaryPressure = useCallback(
    (uid: string) => {
      const stored = samplesByUid.get(uid);
      if (!stored || !projectUid) return;
      // Prefer the previewed (unsaved) result so the user can review the
      // batch outcome in the full single-sample UI and save it there.
      const sample = results.get(uid)?.sample ?? stored;
      const wellSamples = samples
        .filter(s => s.well_uid === sample.well_uid)
        .map(s => (s.uid === uid ? sample : s));
      updateData({ wellSamples });
      const well = data.wells.find(entry => entry.uid === sample.well_uid);
      setSelectedInfo({ sample, well });
      addToHistory(sample);
      const page = isKrMethod(method) ? 'kr-analysis' : 'capillary-pressure';
      navigate(`/${page}?project=${projectUid}`);
    },
    [
      samplesByUid,
      results,
      samples,
      projectUid,
      method,
      updateData,
      data.wells,
      setSelectedInfo,
      addToHistory,
      navigate,
    ]
  );

  return {
    samples,
    samplesByUid,
    methodSamples,
    visibleSamples,
    addToSelection,
    setSelection,
    clearSelection,
    checkedUids,
    checkedCount: runnableCheckedUids.length,
    toggleChecked,
    checkAll,
    checkNone,
    checkVisible,
    method,
    setMethod,
    lambdaMethod,
    setLambdaMethod,
    compareAllMethods,
    setCompareAllMethods,
    krRunMode,
    setKrRunMode,
    methodResultsFor,
    chooseMethod,
    batchStatus,
    progress,
    results,
    statusCounts,
    aggregateFit,
    failedUids,
    preexistingUids,
    savableUids,
    overwriteUids,
    runChecked,
    rerun,
    rerunFailed,
    saveChecked,
    save,
    cancel,
    statusFilter,
    setStatusFilter,
    inspectedUid,
    setInspectedUid,
    pceValue,
    setPce,
    toggleReviewed,
    openInCapillaryPressure,
  };
}

export type MultiLiaoAnalysis = ReturnType<typeof useMultiLiaoAnalysis>;
